import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, TextField, IconButton, Chip, Tooltip,
  Switch, FormControlLabel, CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';

const API = process.env.REACT_APP_API_URL || '';
const ROL_COLORS = { admin: '#ef5350', medico: '#42a5f5', recepcionista: '#66bb6a' };
const ROL_LABELS = { admin: 'Admin', medico: 'Médico', recepcionista: 'Recep.' };

function formatHora(d) {
  const dt = new Date(d);
  const hoy = new Date();
  const esHoy = dt.toDateString() === hoy.toDateString();
  const hora = dt.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  if (esHoy) return hora;
  return `${dt.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })} ${hora}`;
}

export default function ChatPanel({ open, onClose }) {
  const yo = localStorage.getItem('nombre') || '';
  const rolYo = localStorage.getItem('rol') || '';
  const esAdmin = rolYo === 'admin';

  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [chatActivo, setChatActivo] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [cargando, setCargando] = useState(true);
  const ultimoIdRef = useRef(0);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const intervaloRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/chat/estado`).then(r => setChatActivo(r.data.activo)).catch(() => {});
    cargar(true);
    intervaloRef.current = setInterval(() => cargar(false), 3000);
    return () => clearInterval(intervaloRef.current);
  }, [open]);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensajes, open]);

  async function cargar(inicial) {
    try {
      const params = inicial ? {} : { desde_id: ultimoIdRef.current };
      const r = await axios.get(`${API}/api/chat`, { params });
      if (r.data.length > 0) {
        ultimoIdRef.current = r.data[r.data.length - 1].id;
        if (inicial) { setMensajes(r.data); setCargando(false); }
        else setMensajes(prev => [...prev, ...r.data]);
      } else if (inicial) setCargando(false);
    } catch (err) {
      if (err.response?.data?.desactivado) setChatActivo(false);
      if (inicial) setCargando(false);
    }
  }

  async function toggleChat() {
    setToggling(true);
    try {
      const nuevo = !chatActivo;
      await axios.put(`${API}/api/chat/estado`, { activo: nuevo });
      setChatActivo(nuevo);
    } catch {} finally { setToggling(false); }
  }

  async function enviar() {
    if (!texto.trim() || enviando) return;
    setEnviando(true);
    try {
      await axios.post(`${API}/api/chat`, { texto: texto.trim() });
      setTexto('');
      await cargar(false);
    } catch {} finally {
      setEnviando(false);
      inputRef.current?.focus();
    }
  }

  async function eliminar(id) {
    try {
      await axios.delete(`${API}/api/chat/${id}`);
      setMensajes(prev => prev.filter(m => m.id !== id));
    } catch {}
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); }
  }

  const esMio = (m) => m.usuario_nombre === yo;

  if (!esAdmin && !chatActivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100%', gap: 2, p: 3 }}>
        <BlockIcon sx={{ fontSize: 48, color: '#555' }} />
        <Typography variant="h6" sx={{ color: '#666', fontSize: 15 }}>Chat desactivado</Typography>
        <Typography sx={{ color: '#555', fontSize: 13, textAlign: 'center' }}>
          El administrador desactivó el chat.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid var(--c-primario)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: 'var(--c-superficie)', flexShrink: 0, gap: 1 }}>
        <Typography sx={{ fontWeight: 700, color: 'var(--c-acento)', fontSize: 14, flexShrink: 0 }}>
          Chat del equipo
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
          {esAdmin && (
            <FormControlLabel
              control={
                <Switch checked={chatActivo} onChange={toggleChat} disabled={toggling} size="small"
                  sx={{ '& .MuiSwitch-thumb': { bgcolor: chatActivo ? '#66bb6a' : '#ef5350' } }} />
              }
              label={
                <Typography sx={{ fontSize: 10, color: chatActivo ? '#66bb6a' : '#ef5350', fontWeight: 600 }}>
                  {chatActivo ? 'Activo' : 'Inactivo'}
                </Typography>
              }
              labelPlacement="start"
              sx={{ mr: 0, ml: 0 }}
            />
          )}
          {onClose && (
            <IconButton size="small" onClick={onClose}
              sx={{ color: '#777', '&:hover': { color: '#e0e0e0' }, p: 0.3 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {esAdmin && !chatActivo && (
        <Box sx={{ bgcolor: 'rgba(239,83,80,0.1)', borderBottom: '1px solid #ef5350', px: 2, py: 0.8 }}>
          <Typography sx={{ fontSize: 11, color: '#ef9a9a' }}>
            Chat desactivado para otros usuarios.
          </Typography>
        </Box>
      )}

      {/* Mensajes */}
      <Box sx={{
        flex: 1, overflowY: 'auto', p: 1.5,
        display: 'flex', flexDirection: 'column', gap: 1.2,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'var(--c-primario)', borderRadius: 2 },
      }}>
        {cargando && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress size={24} sx={{ color: 'var(--c-acento)' }} />
          </Box>
        )}

        {!cargando && mensajes.length === 0 && (
          <Typography sx={{ color: '#555', textAlign: 'center', mt: 4, fontSize: 13 }}>
            Todavía no hay mensajes.
          </Typography>
        )}

        {mensajes.map((m) => {
          const mio = esMio(m);
          return (
            <Box key={m.id} sx={{ display: 'flex', flexDirection: 'column',
              alignItems: mio ? 'flex-end' : 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2,
                flexDirection: mio ? 'row-reverse' : 'row' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: ROL_COLORS[m.usuario_rol] || '#90a4ae' }}>
                  {m.usuario_nombre}
                </Typography>
                <Chip label={ROL_LABELS[m.usuario_rol] || m.usuario_rol} size="small"
                  sx={{ height: 14, fontSize: 9, px: 0.2,
                    bgcolor: (ROL_COLORS[m.usuario_rol] || '#555') + '33',
                    color: ROL_COLORS[m.usuario_rol] || '#90a4ae' }} />
                <Typography sx={{ fontSize: 10, color: '#555' }}>{formatHora(m.created_at)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4,
                flexDirection: mio ? 'row-reverse' : 'row' }}>
                <Box sx={{
                  maxWidth: 230, px: 1.2, py: 0.7,
                  bgcolor: mio ? 'var(--c-primario)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid var(--c-primario)`,
                  borderRadius: mio ? '14px 3px 14px 14px' : '3px 14px 14px 14px',
                }}>
                  <Typography sx={{ fontSize: 13, color: '#e0e0e0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {m.texto}
                  </Typography>
                </Box>
                {esAdmin && (
                  <Tooltip title="Eliminar">
                    <IconButton size="small" onClick={() => eliminar(m.id)}
                      sx={{ color: '#444', '&:hover': { color: '#ef5350' }, p: 0.2 }}>
                      <DeleteIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })}
        <div ref={endRef} />
      </Box>

      {/* Input */}
      <Box sx={{ p: 1, borderTop: '1px solid var(--c-primario)', flexShrink: 0,
        display: 'flex', gap: 0.8, alignItems: 'flex-end' }}>
        <TextField
          inputRef={inputRef}
          fullWidth multiline maxRows={3}
          placeholder="Mensaje... (Enter envía)"
          value={texto}
          onChange={e => setTexto(e.target.value)}
          onKeyDown={handleKeyDown}
          size="small"
          inputProps={{ style: { color: '#e0e0e0', fontSize: 13 }, maxLength: 500 }}
          sx={{
            '& .MuiOutlinedInput-root fieldset': { borderColor: 'var(--c-primario)' },
            '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'var(--c-acento)' },
            '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: 'var(--c-acento)' },
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
          <Typography sx={{ fontSize: 9, color: texto.length > 450 ? '#ef5350' : '#444' }}>
            {texto.length}/500
          </Typography>
          <IconButton onClick={enviar} disabled={!texto.trim() || enviando}
            size="small"
            sx={{
              bgcolor: 'var(--c-primario)', color: 'var(--c-acento)',
              '&:hover': { bgcolor: 'var(--c-acento)', color: 'var(--c-fondo)' },
              '&.Mui-disabled': { bgcolor: '#1a2a3a', color: '#333' },
              width: 36, height: 36,
            }}>
            <SendIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
